import { useSyncExternalStore } from 'react'

/**
 * Préférences & branding côté navigateur (LOT 5.5).
 *
 * La VÉRITÉ vit en base (GET/PUT /api/v1/me/preferences) — localStorage
 * n'est qu'un CACHE d'amorçage : il évite le flash (thème/accent appliqués
 * avant le premier rendu), puis le serveur corrige si besoin. Une session
 * sur un autre navigateur retrouve donc ses choix.
 */

export type Prefs = Record<string, string>

export const PREF_DEFAULTS: Prefs = {
  theme: 'dark', accent: 'violet', accent_custom: '#7C5CFF', ambience: 'premium',
  transparency: 'normal', motion: 'system', density: 'comfortable', scale: '100',
  radius: 'standard', contrast: 'normal', focus_ring: 'normal',
  sidebar: 'auto', sidebar_close_delay: 'comfortable', home: 'dashboard',
  hour_format: '24', date_format: 'short', relative_time: 'auto',
  alerts_badge: 'all', dashboard_show_tonight: 'true', dashboard_show_unconfigured: 'true',
  context_menu: 'on',
}

export interface Branding {
  app_name: string
  org_name: string
  assets: Record<string, { url: string; v: string } | null>
}
const BRANDING_DEFAULTS: Branding = { app_name: 'Gestion', org_name: 'Live Club Rennes', assets: {} }

interface Store {
  resolved: Prefs      // défauts produit ← organisation ← utilisateur
  defaults: Prefs      // défauts produit ← organisation (pour « réinitialiser »)
  user: Prefs          // surcharges du compte uniquement
  authenticated: boolean
  /** true après la première synchronisation serveur réussie — les pages
   *  d'édition attendent ce signal avant d'initialiser leur snapshot. */
  synced: boolean
  branding: Branding
}

let store: Store = {
  resolved: { ...PREF_DEFAULTS }, defaults: { ...PREF_DEFAULTS }, user: {},
  authenticated: false, synced: false, branding: BRANDING_DEFAULTS,
}
let listeners: Array<() => void> = []
const emit = () => listeners.forEach((l) => l())

export function usePrefsStore(): Store {
  return useSyncExternalStore(
    (cb) => { listeners.push(cb); return () => { listeners = listeners.filter((l) => l !== cb) } },
    () => store,
  )
}

/* ─── application au document ─── */

const AMBIENCE_MAP: Record<string, { intensity: string; ambient: string }> = {
  minimal: { intensity: 'clean', ambient: 'off' },
  discrete: { intensity: 'premium', ambient: 'low' },
  premium: { intensity: 'premium', ambient: 'normal' },
  immersive: { intensity: 'immersive', ambient: 'normal' },
}

const ACCENTS: Record<string, { base: string; bright: string; deep: string }> = {
  violet: { base: '#7C5CFF', bright: '#9B7BFF', deep: '#4C33C9' },
  blue: { base: '#3B82F6', bright: '#6AA5FF', deep: '#1D4ED8' },
  cyan: { base: '#22D3EE', bright: '#67E3F9', deep: '#0E7490' },
  emerald: { base: '#10D9A0', bright: '#4AE7BC', deep: '#047857' },
  amber: { base: '#F5A524', bright: '#FBBF4A', deep: '#B45309' },
}

function shade(hex: string, f: number): string {
  const c = [1, 3, 5].map((i) => Math.max(0, Math.min(255, Math.round(parseInt(hex.slice(i, i + 2), 16) * f))))
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/** Pose les attributs data-* et variables CSS. Idempotent — rappelée à
 *  chaque navigation pour reprendre la main sur les overrides locaux
 *  (panneau démo RH, écrans auth). */
export function applyPrefs(p: Prefs): void {
  const r = document.documentElement
  r.setAttribute('data-theme', p['theme'] ?? 'dark')
  const amb = AMBIENCE_MAP[p['ambience'] ?? 'premium'] ?? AMBIENCE_MAP['premium']!
  r.setAttribute('data-intensity', amb.intensity)
  r.setAttribute('data-ambient', amb.ambient)
  r.setAttribute('data-transparency', p['transparency'] ?? 'normal')
  r.setAttribute('data-density', p['density'] ?? 'comfortable')
  r.setAttribute('data-radius', p['radius'] ?? 'standard')
  r.setAttribute('data-contrast', p['contrast'] ?? 'normal')
  r.setAttribute('data-focus', p['focus_ring'] ?? 'normal')

  const motion = p['motion'] ?? 'system'
  r.setAttribute('data-motion', motion)
  if (motion === 'reduced' || motion === 'minimal') r.setAttribute('data-force-reduced', '1')
  else r.removeAttribute('data-force-reduced')

  const scale = p['scale'] ?? '100'
  r.style.fontSize = scale === '100' ? '' : `${scale}%`

  const accent = p['accent'] ?? 'violet'
  const c = accent === 'custom'
    ? { base: p['accent_custom'] ?? '#7C5CFF', bright: shade(p['accent_custom'] ?? '#7C5CFF', 1.25), deep: shade(p['accent_custom'] ?? '#7C5CFF', 0.62) }
    : (ACCENTS[accent] ?? ACCENTS['violet']!)
  r.style.setProperty('--accent', c.base)
  r.style.setProperty('--accent-bright', c.bright)
  r.style.setProperty('--accent-deep', c.deep)
  r.style.setProperty('--grad-accent', `linear-gradient(135deg, ${c.base} 0%, ${c.deep} 100%)`)
  r.style.setProperty('--glow-accent', `radial-gradient(closest-side, color-mix(in oklab, ${c.base} 55%, transparent), transparent)`)
}

/* ─── branding : favicon, touch icon, titre ─── */

function setIcon(rel: string, href: string | null, type?: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!href) { el?.remove(); return }
  if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el) }
  if (type) el.type = type
  el.href = href
}

export function applyBranding(b: Branding): void {
  setIcon('icon', b.assets['favicon']?.url ?? '/livecontrol-mark.svg', b.assets['favicon'] ? undefined : 'image/svg+xml')
  setIcon('apple-touch-icon', b.assets['touch_icon']?.url ?? b.assets['logo_compact']?.url ?? null)
}

let currentPageTitle = ''
export function setPageTitle(page: string | null): void {
  currentPageTitle = page ?? ''
  const b = store.branding
  document.title = page ? `${page} — ${b.app_name}` : `${b.app_name} — ${b.org_name}`
}

/** Le titre de page posé en dernier — par le shell (registre) ou par l'écran
 *  lui-même (une soirée, une association, un signalement) ; null sur
 *  l'accueil. L'étoile des favoris y lit le nom de la page sans toucher à
 *  ces écrans (24/10/2026). */
export function titreDePageCourant(): string | null {
  return currentPageTitle || null
}

/* ─── amorçage + synchronisation serveur ─── */

const CACHE_KEY = 'gestion.prefs-cache'

/** Synchione : applique le cache AVANT le premier rendu (pas de flash). */
export function bootFromCache(): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (raw) {
      const c = JSON.parse(raw) as { resolved?: Prefs; branding?: Branding }
      if (c.resolved) store = { ...store, resolved: { ...PREF_DEFAULTS, ...c.resolved } }
      if (c.branding) store = { ...store, branding: c.branding }
    }
  } catch { /* cache illisible : défauts produit */ }
  applyPrefs(store.resolved)
  applyBranding(store.branding)
}

function persistCache(): void {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ resolved: store.resolved, branding: store.branding })) } catch { /* plein/indisponible */ }
}

export async function syncFromServer(): Promise<void> {
  try {
    const [prefsRes, brandRes] = await Promise.all([
      fetch('/api/v1/me/preferences', { credentials: 'same-origin' }).then((r) => r.json()),
      fetch('/api/v1/public/branding', { credentials: 'same-origin' }).then((r) => r.json()),
    ]) as [{ authenticated: boolean; defaults: Prefs; preferences: Prefs; resolved: Prefs }, Branding]
    store = {
      resolved: { ...PREF_DEFAULTS, ...prefsRes.resolved },
      defaults: { ...PREF_DEFAULTS, ...prefsRes.defaults },
      user: prefsRes.preferences,
      authenticated: prefsRes.authenticated,
      synced: true,
      branding: brandRes,
    }
    applyPrefs(store.resolved)
    applyBranding(store.branding)
    setPageTitle(currentPageTitle || null)
    persistCache()
    emit()
  } catch { /* hors ligne : le cache local reste en vigueur */ }
}

/** Aperçu immédiat (non sauvegardé) — Apparence l'utilise pour prévisualiser. */
export function previewPrefs(overrides: Prefs): void {
  applyPrefs({ ...store.resolved, ...overrides })
}

/** Enregistre les surcharges UTILISATEUR (remplace l'ensemble). En cas de
 *  refus, le MESSAGE du serveur remonte (ex. contraste d'accent invalide) —
 *  un « Enregistrement refusé » muet ressemblait à un bug (recette 5.5). */
export async function saveUserPrefs(user: Prefs, csrf: (m: string, p: string, b?: unknown) => Promise<{ status: number; data: unknown }>): Promise<{ ok: boolean; message?: string }> {
  const r = await csrf('PUT', '/api/v1/me/preferences', { preferences: user })
  if (r.status !== 200) {
    const message = (r.data as { message?: string }).message
    return { ok: false, message: message ?? 'Enregistrement refusé par le serveur.' }
  }
  const data = r.data as { preferences: Prefs; resolved: Prefs }
  store = { ...store, user: data.preferences, resolved: { ...PREF_DEFAULTS, ...data.resolved } }
  applyPrefs(store.resolved)
  persistCache()
  emit()
  return { ok: true }
}

/** Modifie UNE OU PLUSIEURS préférences utilisateur (fusion avec les
 *  surcharges existantes). Sans session : application locale temporaire
 *  (cache navigateur) — jamais d'écriture serveur anonyme. */
export async function setUserPref(
  patch: Prefs,
  csrf: (m: string, p: string, b?: unknown) => Promise<{ status: number; data: unknown }>,
): Promise<boolean> {
  if (!store.authenticated) {
    store = { ...store, resolved: { ...store.resolved, ...patch } }
    applyPrefs(store.resolved)
    persistCache()
    emit()
    return true
  }
  const r = await saveUserPrefs({ ...store.user, ...patch }, csrf)
  return r.ok
}

/** Route d'accueil selon la préférence (après connexion). */
export function homeRoute(resolved: Prefs): string {
  const home = resolved['home'] ?? 'dashboard'
  if (home === 'last') {
    try { return localStorage.getItem('gestion.last-route') ?? '/' } catch { return '/' }
  }
  if (home === 'rh-access') return '/equipe/rh'   // ancienne valeur stockée — route canonique SIRH
  return home === 'dashboard' ? '/' : `/${home}`
}
